/**
 * The user's profile, as Phase 1 stored it in `localStorage` — a one-time migration source now,
 * not the app's identity story.
 *
 * `profiles` in Supabase is the authority on who a user is and which block they're on.
 * `SetupScreen` (`src/screens/SetupScreen.tsx`) reads `readLocalProfile()` once, via a `useState`
 * initialiser, to pre-fill its form when the corresponding server-side field is still null — and
 * calls `clearLocalProfile()` only after that form has successfully saved to `profiles`. Reading
 * never clears as a side effect, so it stays safe to call speculatively up until that explicit
 * clear.
 *
 * `writeLocalProfile` has no live application caller — Phase 1's `Onboarding` component, the only
 * app code that ever wrote this key, is retired now that `SetupScreen` absorbs its flow. It
 * remains exported because `local-profile.test.ts` and `local-profile-migration.test.ts` use it to
 * seed a Phase-1-shaped fixture directly, which is the whole point of those tests.
 *
 * The storage key below is a compatibility contract regardless: it is the same key Phase 1's
 * `Onboarding` component wrote, which is what lets a Phase 1 user's data be found at all.
 *
 * `rosterType: 'BLOCK'` is the only variant Phase 1 knows about. FWA and BLOCK_PARTIAL arrive in
 * Phase 3 as *additional* values of `rosterType`, not as a redesign of this shape — so the field
 * exists now, even though it can only ever hold one value today.
 */

import { ACTAS_ROSTER } from '../data/roster-config'
import type { BlockCode, WeekStart } from '../engine/types'

export interface LocalProfile {
  rosterType: 'BLOCK'
  blockCode: BlockCode
  /**
   * Optional on purpose. A preference, not identity: `readLocalProfile`
   * defaults it to `'SUNDAY'` whether it is absent or malformed, rather than
   * invalidating an otherwise-good profile the way a bad `blockCode` does.
   * See that function's docstring.
   */
  weekStart?: WeekStart
}

const STORAGE_KEY = 'shiftswap.local-profile.v1'

// Derived from the roster config, not hand-repeated: TypeScript catches a
// *wrong* block code here but not a *missing* one, so a literal list would
// silently reject the stored profile of everyone on a block a future roster
// change adds, throwing them back to onboarding with no error.
const BLOCK_CODES: readonly BlockCode[] = ACTAS_ROSTER.blockOrder

function isBlockCode(value: unknown): value is BlockCode {
  return typeof value === 'string' && (BLOCK_CODES as readonly string[]).includes(value)
}

function isWeekStart(value: unknown): value is WeekStart {
  return value === 'SUNDAY' || value === 'MONDAY'
}

/**
 * Is `value` a well-formed `LocalProfile`? Checked field-by-field rather
 * than trusted from `JSON.parse`, because a malformed `blockCode` reaching
 * the roster engine fails far from here — inside `blockIndex`, with no clue
 * that the real problem was a stale or hand-edited `localStorage` entry.
 *
 * `weekStart` is deliberately not checked here. `rosterType` and `blockCode`
 * are identity — wrong, and the whole profile is unusable. `weekStart` is a
 * preference; `readLocalProfile` normalises it separately, after this guard
 * passes, so a missing or malformed value never invalidates an otherwise
 * good profile.
 */
function isLocalProfile(value: unknown): value is LocalProfile {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return candidate.rosterType === 'BLOCK' && isBlockCode(candidate.blockCode)
}

/**
 * Reads the stored profile, or `null` if there isn't one, it's malformed, or
 * there is no `localStorage` to read from at all (every test in this
 * project, and any non-browser render, runs in that last environment).
 *
 * Checked via `typeof localStorage === 'undefined'` rather than `typeof
 * window === 'undefined'` — `window` is a banned bare global under
 * `boundaries.test.ts`'s rules elsewhere in this codebase, and the
 * `localStorage` check is what this function actually depends on.
 */
export function readLocalProfile(): LocalProfile | null {
  if (typeof localStorage === 'undefined') return null

  const raw = localStorage.getItem(STORAGE_KEY)
  if (raw === null) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }

  if (!isLocalProfile(parsed)) return null

  // weekStart defaults here rather than being enforced by isLocalProfile —
  // see that function's docstring. A stored profile with no weekStart, or
  // one holding neither 'SUNDAY' nor 'MONDAY', still reads back valid.
  return {
    rosterType: parsed.rosterType,
    blockCode: parsed.blockCode,
    weekStart: isWeekStart(parsed.weekStart) ? parsed.weekStart : 'SUNDAY',
  }
}

/** Writes the profile. A no-op where there is no `localStorage` to write to. */
export function writeLocalProfile(profile: LocalProfile): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(profile))
}

/** Removes the stored profile, if any. A no-op where there is no `localStorage`. */
export function clearLocalProfile(): void {
  if (typeof localStorage === 'undefined') return
  localStorage.removeItem(STORAGE_KEY)
}
